import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, SectionTitle, TimeAgo, Kpi } from "@/components/sov";
import { useAuth } from "@/contexts/AuthContext";
import { Zap, Webhook, Save, Send, Check, AlertTriangle, ShieldCheck } from "lucide-react";

const PRIO_COLOR = {
  sovereign: "#ff7a6e",
  critical:  "#ff9180",
  warn:      "#f5a73a",
  info:      "#9bb1c8",
};

const TRIGGER_LABELS = {
  "auth.risk.high":     "Auth · Elevated risk score",
  "auth.failed.spike":  "Auth · Brute-force spike",
  "auth.mfa.disabled":  "Auth · MFA disabled",
  "admin.action":       "Admin · Configuration change",
  "incident.critical":  "Incident · Critical opened",
  "connector.changed":  "Connector · Status change",
  "vessel.degraded":    "Vessel · Health degraded",
};

const Pulse = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [pulses, setPulses] = useState([]);
  const [summary, setSummary] = useState({ unack_total: 0, unack_by_priority: {}, total_24h: 0 });
  const [filter, setFilter] = useState("all");
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const refresh = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/pulse?limit=100"),
        api.get("/pulse/summary"),
      ]);
      setPulses(p.data.pulses);
      setSummary(s.data);
      if (isAdmin) {
        const c = await api.get("/pulse/config");
        setConfig(c.data);
      }
    } catch {}
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [isAdmin]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const { data } = await api.put("/pulse/config", config);
      setConfig(data);
    } finally {
      setSavingConfig(false);
    }
  };

  const sendTest = async (priority) => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/pulse/test", { priority });
      setTestResult(data);
      refresh();
    } catch (e) {
      setTestResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  const ack = async (id) => {
    await api.post(`/pulse/${id}/ack`);
    refresh();
  };

  const filtered = filter === "all" ? pulses : pulses.filter((p) => p.priority === filter);

  return (
    <div className="space-y-6" data-testid="pulse-page">
      <SectionTitle sub="// SENTINEL · DIRECTORATE NOTIFICATION ENGINE">
        Sentinel Pulse
      </SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Kpi
          testid="kpi-unack"
          label="Unacknowledged"
          value={summary.unack_total}
          sub={summary.unack_by_priority?.sovereign ? "⚜ sovereign-priority pending" : "queue clean"}
          accent
        />
        <Kpi
          testid="kpi-sovereign"
          label="Sovereign"
          value={summary.unack_by_priority?.sovereign || 0}
          sub="director-grade signals"
        />
        <Kpi
          testid="kpi-critical"
          label="Critical"
          value={summary.unack_by_priority?.critical || 0}
          sub="dispatched · webhook"
        />
        <Kpi
          testid="kpi-channels"
          label="Channels"
          value={config?.webhook_url ? 2 : 1}
          sub={config?.webhook_url ? "in-app + webhook" : "in-app only"}
        />
      </div>

      {/* CONFIG (admin) */}
      {isAdmin && config && (
        <Panel
          testid="panel-pulse-config"
          title="Director's Coordinate"
          code="§ S.A"
          action={
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
              <Webhook size={11} className="text-gold-300" />
              webhook channel
            </span>
          }
        >
          <div className="p-5 space-y-4">
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">
                Webhook URL — Slack/Discord/Teams/Bridge
              </label>
              <input
                data-testid="config-webhook-url"
                className="sov-input mt-1"
                placeholder="https://hooks.slack.com/services/..."
                value={config.webhook_url}
                onChange={(e) => setConfig({ ...config, webhook_url: e.target.value })}
              />
              <div className="font-mono text-[9px] text-ivory-3 mt-1.5">
                Sovereign + Critical priorities are dispatched here. Info + warn stay in-app.
              </div>
            </div>

            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-2">
                Trigger Catalogue
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {Object.entries(TRIGGER_LABELS).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 px-3 py-2 border border-[color:var(--gold-line)] cursor-pointer hover:bg-[rgba(200,162,75,0.04)]" data-testid={`trigger-${k}`}>
                    <input
                      type="checkbox"
                      checked={config.triggers_enabled?.[k] !== false}
                      onChange={(e) => setConfig({
                        ...config,
                        triggers_enabled: { ...(config.triggers_enabled || {}), [k]: e.target.checked },
                      })}
                      className="accent-[var(--gold-300)]"
                    />
                    <span className="font-mono text-[10px] tracking-[0.1em] text-ivory-2">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button onClick={saveConfig} disabled={savingConfig} className="btn-gold" data-testid="config-save">
                <Save size={11} />
                {savingConfig ? "Saving…" : "Persist Config"}
              </button>
              <button onClick={() => sendTest("sovereign")} disabled={testing} className="btn-ghost" data-testid="test-sovereign">
                <Send size={11} />
                Send Sovereign Test
              </button>
              <button onClick={() => sendTest("critical")} disabled={testing} className="btn-ghost" data-testid="test-critical">
                <Send size={11} />
                Send Critical Test
              </button>
              {testResult && (
                <span className="font-mono text-[10px] text-ivory-2 ml-2">
                  {testResult.error ? (
                    <span style={{ color: "#ff7a6e" }}>{testResult.error}</span>
                  ) : (
                    <span style={{ color: "#6cc88f" }}>
                      ✓ Pulse {testResult.pulse?.id?.slice(0, 8)} — webhook: {testResult.pulse?.dispatch_results?.webhook?.ok ? "delivered" : "in-app only"}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </Panel>
      )}

      {/* PULSE STREAM */}
      <Panel
        testid="panel-pulse-stream"
        title="Pulse Stream"
        code="§ S.B"
        action={
          <div className="flex items-center gap-2">
            {["all", "sovereign", "critical", "warn", "info"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`btn-ghost ${filter === f ? "ring-1 ring-[color:var(--gold-300)] text-gold-100" : ""}`}
                data-testid={`pulse-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        }
      >
        <div className="divide-y divide-[color:var(--gold-line)]">
          {filtered.length === 0 && (
            <div className="p-12 text-center font-serif italic text-[14px] text-ivory-3" data-testid="pulse-empty">
              <ShieldCheck size={32} className="text-gold-300 mx-auto mb-3 opacity-60" />
              The bridge is calm. Sentinel detects no pulses matching this filter.
            </div>
          )}
          {filtered.map((p) => (
            <div
              key={p.id}
              className={`p-5 transition ${p.ack ? "opacity-50" : ""}`}
              style={{ borderLeft: `3px solid ${PRIO_COLOR[p.priority]}` }}
              data-testid={`pulse-${p.id.slice(0, 6)}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span
                      className="font-mono text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 border"
                      style={{ color: PRIO_COLOR[p.priority], borderColor: `${PRIO_COLOR[p.priority]}55` }}
                    >
                      {p.priority}
                    </span>
                    <span className="font-mono text-[9px] tracking-[0.2em] text-ivory-3 uppercase">
                      {p.category}{p.trigger ? ` · ${p.trigger}` : ""}
                    </span>
                    <TimeAgo iso={p.timestamp} />
                  </div>
                  <div className="font-display text-[15px] tracking-[0.06em] text-gold-100 mt-1">
                    {p.title}
                  </div>
                  <div className="font-serif text-[13px] text-ivory-2 italic mt-1 leading-snug">
                    "{p.message}"
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    {p.dispatched_to?.map((d) => (
                      <span
                        key={d}
                        className="font-mono text-[9px] tracking-[0.2em] text-ivory-3 uppercase flex items-center gap-1"
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{
                          background: p.dispatch_results?.[d]?.ok ? "#6cc88f" : "#ff7a6e",
                        }} />
                        {d}
                      </span>
                    ))}
                    {p.ack && (
                      <span className="font-mono text-[9px] tracking-[0.2em] text-[#6cc88f] uppercase">
                        ✓ ack by {p.ack_by} <TimeAgo iso={p.ack_at} />
                      </span>
                    )}
                  </div>
                </div>
                {!p.ack && (
                  <button onClick={() => ack(p.id)} className="btn-ghost shrink-0" data-testid={`ack-${p.id.slice(0,6)}`}>
                    <Check size={11} /> Ack
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

export default Pulse;
